import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { postRequest } from '../../../helpers/request'
import { appApi } from '../../../utils/endpoints/appApi'
import { onChangeStatus } from '../../../config/redux/slice/bankUserSlice'
import { onChangeStatus as onChangeStatusNotap } from '../../../config/redux/slice/notapUserSlice'
import { onChangeStatus as onChangeStatusTransferee } from '../../../config/redux/slice/transfereeUserSlice'
import { clearAll, setErrorMessageUser, setLoadingUser  } from '../../../config/redux/slice/appSlice'
import { Spinner } from '../../components'
import { showStatusChangeConfirmationFunc } from '../../../config/redux/slice/appSlice'
import { ROLES } from '../../../constants';

function StatusChangeConfirmation({ status, btnText, certificateID }) {
    const dispatch = useDispatch();

    const { userLoading, userErrorMsg } = useSelector((state) => state.app);
    const { token, userData: { user_role } } = useSelector((state) => state.authData)

    const schema = useMemo(() => {
      const schema = z.object({
        swift_code: z.string().optional(),
        comment: z.string().optional(),
      }).refine((data) => user_role === ROLES.BANK.TEXT ? !!data.swift_code : true, {
          message: '*Swift code is required.',
          path: ["swift_code"]
      }).refine((data) => user_role === ROLES.BANK.TEXT ? true : !!data.comment, {
        message: "*Comment is required",
        path: ["comment"]
      });

      return schema;
    }, [user_role]);

    const { register, handleSubmit, formState: {errors} } = useForm({ mode: 'onTouched', resolver: zodResolver(schema) });

    const closeModal = useCallback(() => {
        dispatch(clearAll({}))
        dispatch(showStatusChangeConfirmationFunc({ modal: false }));
    }, [dispatch]);

    const handleConfirmAction = async (data) => {
        dispatch(clearAll({}));

        const URL_BY_ROLE = {
          [ROLES.BANK.TEXT]: appApi.bankChangeStatus,
          [ROLES.TRANSFEREE.TEXT]: appApi.transfereeChangeStatus,
        };

        const apiData = {
          comment: data.comment,
          swift_code: data.swift_code,
          certificate_id: certificateID,
          status: URL_BY_ROLE[user_role] ? undefined : status,
          comment_mode: URL_BY_ROLE[user_role] ? undefined : 'EXTERNAL'
        };

        const url = URL_BY_ROLE[user_role] ?? appApi.notapChangeStatus;
        const response = await postRequest(token, url, apiData, dispatch, setLoadingUser, "", setErrorMessageUser);

        if (!response || response.status >= 400) return;
        
        dispatch(showStatusChangeConfirmationFunc({ modal: false }));

        if (user_role === ROLES.BANK.TEXT) {
          return dispatch(onChangeStatus({
            message: response.data.message, 
            status, 
            code: data.swift_code
          }));
        }

        if (user_role ===ROLES.TRANSFEREE.TEXT) {
          return dispatch(onChangeStatusTransferee({ message: response.data.message, status }));
        }

        dispatch(onChangeStatusNotap({ message: response.data.message, status }));
    };

    return (
      <div 
          className="modal fade show" 
          id="basicModal" 
          tabIndex="-1"
          aria-labelledby="exampleModalCenterTitle"
          aria-hidden="true"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.3)", overflowY: 'auto' }}
      >
        <div style={{overflowY: 'auto'}} className="modal-dialog modal-dialog-centered modal-md" role="document">
          <form onSubmit={handleSubmit(handleConfirmAction)}>
            <div className="modal-content">
                <div className="modal-header px-4">
                  <h5 className="modal-title" id="exampleModalCenterTitle">Status Change Confirmation</h5>
                  
                  <button 
                    type="button" 
                    className="close" 
                    data-dismiss="modal" 
                    aria-label="Close"
                    onClick={closeModal}
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
                
                <div className='modal-body'>
                  {userErrorMsg && (
                    <div className="px-4 px-md-7 mb-4">
                      <div className="alert alert-danger" role="alert">
                        {userErrorMsg}
                      </div>
                    </div>
                  )}

                  {user_role === ROLES.BANK.TEXT && (
                      <div className="form-group">
                        <label htmlFor="comment">Swift Code</label>
                        
                        <input 
                            type="text" 
                            className="form-control input-lg" 
                            id="swift_code"
                            {...register("swift_code")} 
                        />
                          
                        {errors.swift_code && <div className="text-danger small mt-1">{errors.swift_code.message}</div>}
                      </div>
                  )}

                  <div className="form-group">
                      <label htmlFor="comment">Comment</label>
                      <textarea 
                        style={{resize: 'none'}}
                        className="form-control" 
                        id="comment" 
                        rows="3"
                        {...register("comment")}
                      ></textarea>
                      {errors.comment && <div className="text-danger small mt-1">{errors.comment.message}</div>}
                  </div>
                </div>
                
                <div className="modal-footer px-4 d-flex align-items-center">
                  <button disabled={userLoading} onClick={closeModal} type="button" className="btn btn-smoke" data-dismiss="modal">
                    Cancel
                  </button>

                  <div>
                    {userLoading ? <Spinner/> : <button type="submit" className="btn btn-danger">{btnText}</button>}
                  </div>
                </div>
            </div>
          </form>
        </div>
      </div>
    );
}

export default StatusChangeConfirmation;
